import type { AccountTreeNode } from './buildAccountTree';

/**
 * An account whose balance sits on the wrong side of its own type.
 *
 * `displayTetri` is already contra-adjusted and in the account's natural
 * direction, so a negative on a **postable** node means exactly one thing: this
 * account is in credit when it should be in debit, or the reverse. That is the
 * same condition the server raises `ASSET_CREDIT_BALANCE` and
 * `LIABILITY_EQUITY_DEBIT_BALANCE` for on the statements.
 */
export type AbnormalBalance = {
    accountId: string;
    accountCode: string;
    tetri: number;
};

/**
 * The abnormal balances currently on screen (BOS-163).
 *
 * The Chart of Accounts painted "Cash on hand ₾ −1.00" and "Cash in bank
 * ₾ −3,700.00" red and said nothing else; the advisory that explains what red
 * means appeared only on the Statements tab, which a reader looking at the
 * chart has no reason to visit. This derives the same fact from what is already
 * rendered — no second query, and nothing to disagree with the rows.
 *
 * Group (header) nodes are skipped: a header's figure is a rollup, and a
 * negative rollup is a consequence of its children rather than a fact about an
 * account anyone can act on. Archived accounts are skipped too — an archived
 * account cannot be posted to, so its balance is history, not a live problem.
 */
export const collectAbnormalBalances = (
    roots: AccountTreeNode[],
): AbnormalBalance[] => {
    const found: AbnormalBalance[] = [];

    const walk = (nodes: AccountTreeNode[]): void => {
        for (const node of nodes) {
            if (
                node.account.isPostable &&
                !node.isArchived &&
                node.displayTetri < 0
            ) {
                found.push({
                    accountId: node.account.id,
                    accountCode: node.account.code,
                    tetri: node.displayTetri,
                });
            }
            walk(node.children);
        }
    };

    walk(roots);
    return found;
};
